<script setup lang="ts">
// A plan's own permanent page (#227): the full write-up, every item's detail, decision history and
// — for an `os_change` plan — the proposed workflow (`PlanGraph`), reachable at `/plans/:id`.
// A plan of any other kind has no items, so it renders its summary and raw payload and no graph.
// An `update_*` item is shown as a diff against the record it would change (#239): the current
// wording beside the proposed one, so the CEO can see what actually changes rather than read a
// wall of replacement text. A `create_task` item has nothing to diff against and stays plain. A
// member, role or team create reads every field as added beside nothing, and a delete reads the
// record's current values beside nothing proposed (#1297). Once the plan has been applied, each item
// says how applying it ended, and an apply that stopped on an item shows what stopped it beside that
// item, so an approved change that never landed does not read as decided and done (#1690).
//
// A plan waiting on the CEO is decided here too (#1984): Approve, Reject and Request changes, sent through
// the gate card's own endpoint (`api.decideGate`) for the decision `GET /plans/:id` names as open
// (`openGate`), so the plan-decision bridge behaves exactly as it does for a decision on the card.
// When that decision was already taken somewhere else while this page was open, the server refuses the
// click (`gate_not_waiting`) and writes nothing; the page then says so, reads the plan again and offers
// no buttons for that decision (#1985).
//
// Every surface, badge, heading, payload, field list, button and empty state is drawn by the kit
// (@zerohuman/ui), and the layout by theme utilities: the screen states no colour, size or spacing of
// its own (#1233). State still reads off the DOM without a colour — `data-item-change` and
// `data-diff-status` carry it, and each pill says it in words beside its glyph.
import { useStorage } from '@vueuse/core'
import {
    Label,
    type StatusTone,
    Textarea,
    ZhButton,
    ZhJson,
    ZhKeyValue,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusPill,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
    api,
    type CeoQuestion,
    type CeoQuestionAnswer,
    ceoQuestions,
    type GateDecision,
    type GateRow,
    PLAN_STATUS_AWAITING_CEO,
    type PlanDetail,
    type PlanItem,
    type PlanItemResult,
    type TaskRow,
} from '@/api'
import FirstRunHint from '@/components/FirstRunHint.vue'
import PlanGraph from '@/components/PlanGraph.vue'
import ToolConnectionsWarning from '@/components/ToolConnectionsWarning.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import { clearGateDecisionDraft, gateDecisionDraftKey } from '@/lib/gateDecisionDraft'
import { reportNeedsYouItemViewed } from '@/lib/needsYouReport'
import {
    currentMemberFields,
    currentRoleFields,
    currentTaskFields,
    currentTeamFields,
    diffPlanItem,
    diffsAgainstNothing,
    type PlanDiffField,
    type PlanDiffStatus,
    planItemDetails,
    planItemNote,
} from '@/lib/planDiff'
import {
    buildPlanGraph,
    changeVerbOf,
    isTaskPlanItem,
    matchesPlanTarget,
    str,
    touchesExistingTask,
} from '@/lib/planGraph'
import { missingToolConnections, planProposedTools } from '@/lib/toolConnections'
import { viewedOncePerPage } from '@/lib/viewedOncePerPage'

const route = useRoute()
const plan = ref<PlanDetail | null>(null)
const tasks = ref<TaskRow[]>([])
const error = ref<string | null>(null)
/** Tasks this plan changes that no lookup could resolve: named here rather than dropped in silence. */
const missingTasks = ref<string[]>([])
/**
 * The tool types this enterprise has connected, or `null` while they are unknown — unread, or the read
 * failed. Unknown stays silent: a warning invented out of a failed lookup would name every tool the plan
 * asks for, which is worse than saying nothing (os#231).
 */
const connectedToolTypes = ref<string[] | null>(null)
/**
 * The record each `update_*` or `delete_*` item would change, as it stands today, keyed by the item's
 * handle (#239, #1297); a member, role or team create is keyed to an empty record, so it reads as
 * added. An item with no entry here — a task creation, and anything whose record could not be read —
 * renders plainly, so a missing read costs the diff, never the proposal itself.
 */
const currentRecords = ref<Record<string, Record<string, unknown>>>({})
const isMobile = useIsMobile()

/** One `GET /tasks` page. */
const TASK_PAGE_SIZE = 200
/** A guard against a server that never stops paging — not a ceiling on what a plan can resolve. */
const MAX_TASK_PAGES = 50

/**
 * The current graph the plan is proposed against, every page of it. A single page would leave an
 * `update_task`/`delete_task` item whose target sits past it resolving to nothing, so the item —
 * and possibly the whole graph — would vanish with nothing said.
 */
async function loadTasks(): Promise<TaskRow[]> {
    const first = await api.tasks({ page: 1, limit: TASK_PAGE_SIZE })
    const rows = [...(first?.data ?? [])]
    const pages = Math.min(Math.max(Number(first?.pages) || 1, 1), MAX_TASK_PAGES)
    for (let page = 2; page <= pages; page += 1) {
        const next = await api.tasks({ page, limit: TASK_PAGE_SIZE })
        const rest = next?.data ?? []
        if (rest.length === 0) break
        rows.push(...rest)
    }
    return rows
}

/**
 * A task's own full record, read once per page load. `GET /tasks` sends a trimmed projection of each
 * row, so the diff below reads the single-task record instead — and the graph, which asks for the
 * same tasks, reuses this rather than fetching them twice.
 */
// The read in flight is what is cached, not its result, so two items naming the same task read it
// once even when they are resolved side by side.
const taskDetails = new Map<string, Promise<TaskRow | null>>()
async function readTask(id: string): Promise<TaskRow | null> {
    try {
        return (await api.task(id)) ?? null
    } catch {
        return null
    }
}
function taskDetail(id: string): Promise<TaskRow | null> {
    const pending = taskDetails.get(id) ?? readTask(id)
    taskDetails.set(id, pending)
    return pending
}

/** The task an item names, fetched on its own when the page walk above never turned it up. */
async function fetchTouchedTask(item: PlanItem): Promise<TaskRow | null> {
    const taskId = str(item.taskId)
    if (taskId) {
        const detail = await taskDetail(taskId)
        if (detail) return detail
    }
    const slug = str(item.slug)
    if (!slug) return null
    // `GET /tasks` filters on `slug` directly (`OrgService.queryTasks`: `task.slug LIKE :slug`), so
    // this narrows to slug matches rather than the broader `search`, which also matches name and
    // assignee role and could crowd the one task out of the page. The match is still checked here,
    // because the server's filter is a LIKE.
    const page = await api.tasks({ slug, limit: TASK_PAGE_SIZE }).catch(() => null)
    return page?.data?.find(task => task.slug === slug) ?? null
}

/**
 * Whatever the plan changes but the task list didn't carry: resolved one by one, so the graph is
 * never quietly short of a step. Anything still unresolved is named on the page.
 */
async function resolveTouchedTasks(planItems: PlanItem[]): Promise<void> {
    const missing: string[] = []
    for (const item of planItems) {
        if (!touchesExistingTask(item)) continue
        if (tasks.value.some(task => matchesPlanTarget(item, task))) continue
        const found = await fetchTouchedTask(item)
        if (found) tasks.value = [...tasks.value, found]
        else missing.push(titleOf(item))
    }
    missingTasks.value = missing
}

/**
 * One record read by id and mapped to comparison fields — the shape `role`, `team` and `member` each
 * follow below — or null when there is no id to read or the read fails.
 */
async function currentFieldsOf<T>(
    id: string | null,
    load: (id: string) => Promise<T>,
    toFields: (record: T) => Record<string, unknown>,
): Promise<Record<string, unknown> | null> {
    if (!id) return null
    const record = await load(id).catch(() => null)
    return record ? toFields(record) : null
}

/** The record an `update_*` item names, as it stands today, or null when nothing readable names it. */
async function currentRecordOf(item: PlanItem): Promise<Record<string, unknown> | null> {
    const subject = subjectOf(item)
    if (subject === 'task') {
        // The list only says which task the item names; the comparison is always against that task's
        // own full record. `GET /tasks` trims a definition to its goal, so diffing the row a failed
        // single-task read left behind would call every guardrail and tool the plan keeps "removed" —
        // a read that fails costs the diff (the item renders plainly), never the truth of it.
        const taskId = str(item.taskId) ?? tasks.value.find(row => matchesPlanTarget(item, row))?.id ?? null
        const task = taskId ? await taskDetail(taskId) : null
        return task ? currentTaskFields(task) : null
    }
    if (subject === 'role') return currentFieldsOf(str(item.roleId), api.role, currentRoleFields)
    if (subject === 'team') return currentFieldsOf(str(item.teamId), api.team, currentTeamFields)
    if (subject === 'member') return currentFieldsOf(str(item.memberId), api.member, currentMemberFields)
    // Anything else — an item naming its target by plan-local handle alone, whose record does not exist
    // yet — keeps the plain rendering rather than a diff against a half-known record.
    return null
}

/**
 * What every `update_*` and `delete_*` item in this plan would change, as it stands today (#239,
 * #1297). A task is read as its own full record rather than off the `GET /tasks` projection, which
 * trims a definition down to its goal — a diff built on that would call every guardrail and tool the
 * plan keeps "removed". A member, role or team create has no record yet, so it is read against none.
 */
async function resolveCurrentRecords(planItems: PlanItem[]): Promise<void> {
    // Read together rather than one after another: a plan touching several records otherwise holds
    // the whole page behind a queue of round trips that have nothing to do with each other.
    const existing = planItems.filter(item => changeOf(item) === 'changed' || changeOf(item) === 'removed')
    const resolved = await Promise.all(existing.map(async item => [item.handle, await currentRecordOf(item)] as const))
    const created = planItems.filter(diffsAgainstNothing).map(item => [item.handle, {}] as const)
    currentRecords.value = Object.fromEntries([
        ...created,
        ...resolved.filter((entry): entry is [string, Record<string, unknown>] => entry[1] !== null),
    ])
}

/**
 * The tool types this enterprise has a connection for, read live rather than stored on the plan, so the
 * warning below clears itself the moment the CEO makes the connection. A failed read leaves the list
 * unknown (see `connectedToolTypes`) instead of pretending nothing is connected.
 */
async function loadConnectedToolTypes(): Promise<void> {
    try {
        const rows = await api.toolBindings()
        connectedToolTypes.value = rows.filter(row => row.connected !== false).map(row => row.toolType)
    } catch {
        connectedToolTypes.value = null
    }
}

onMounted(async () => {
    void loadConnectedToolTypes()
    try {
        const [detail, taskList] = await Promise.all([
            api.getPlan(String(route.params.id)),
            loadTasks().catch(() => [] as TaskRow[]),
        ])
        plan.value = detail
        tasks.value = taskList
        await resolveTouchedTasks(detail.items ?? [])
        await resolveCurrentRecords(detail.items ?? [])
    } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load plan'
    }
})

const items = computed<PlanItem[]>(() => plan.value?.items ?? [])

/**
 * os#231: the connections this plan needs that nobody has made. Named before sign-off, not when the
 * first run fails. Empty while the connected list is unknown, and empty once every tool is connected —
 * `ToolConnectionsWarning` renders nothing either way. The plan can still be approved regardless.
 */
const missingConnections = computed(() =>
    connectedToolTypes.value === null
        ? []
        : missingToolConnections(planProposedTools(items.value), connectedToolTypes.value),
)

/** The existing tasks this plan touches: the graph's own scope. */
const scopeTasks = computed(() =>
    tasks.value.filter(task => items.value.some(item => touchesExistingTask(item) && matchesPlanTarget(item, task))),
)

/**
 * Everything else the enterprise runs, offered as context. `buildPlanGraph` keeps only the ones that
 * hand off to (or from) a task in scope — immediate neighbours, not the whole enterprise.
 */
const relatedTasks = computed(() => tasks.value.filter(task => !scopeTasks.value.includes(task)))

const graph = computed(() => buildPlanGraph(items.value, scopeTasks.value, relatedTasks.value))

/** Only an `os_change` plan has items, and only its task items have a workflow to draw. */
const showGraph = computed(() => items.value.some(isTaskPlanItem) && graph.value.nodes.length > 0)

/**
 * What applying each item came to, by handle (#1690): read off the plan's own results, so an item that failed
 * carries the error that stopped the apply beside it. Empty until the plan has been applied.
 */
const results = computed<Record<string, PlanItemResult>>(() =>
    Object.fromEntries((plan.value?.itemResults ?? []).map(result => [result.handle, result])),
)

/** The item the apply stopped on, if it stopped on one: what the page names at the top of a failed plan. */
const failedResult = computed(() => (plan.value?.itemResults ?? []).find(result => result.outcome === 'failed') ?? null)

/**
 * Whether the change was not saved: an item failed, or the plan's apply ended in `error` with no item to name, as the
 * thread is told then too ("Its apply ended in error."), so the page never reads as done where the chat says not saved.
 */
const applyFailed = computed(() => failedResult.value !== null || plan.value?.status === 'error')

/** The one kind of plan the harness applies itself: an org change to tasks, roles, teams and members. */
const ORG_CHANGE_KIND = 'os_change'

/**
 * Whether this plan was approved and left to the task that proposed it (#1986). Approving a plan of any kind but an
 * org change only records the decision: the plan-decision bridge skips the apply (`plan.apply_deferred`,
 * `not_an_org_change`) and nothing ever moves the plan on from `approved`, so the page says what the approval did.
 * Read off the plan the page holds, by the bridge's own test (`kind !== 'os_change'`), so it shows right after an
 * approval re-reads the plan and on every later visit alike. An org-change plan never shows it, deferred or not.
 */
const applyDeferred = computed(() => plan.value?.status === 'approved' && plan.value.kind !== ORG_CHANGE_KIND)

/** This item's result once the plan has been applied, or null before then. */
function resultOf(item: PlanItem): PlanItemResult | null {
    return results.value[item.handle] ?? null
}

/** Whether the plan has been applied, so each item has an outcome to show. */
const applyRecorded = computed(() => Boolean(plan.value?.itemResults?.length))

/**
 * How applying this item ended, said in words beside its glyph: applied, failed, or not applied at all because the
 * apply stopped on an earlier item. Shown only once the plan has been applied (`applyRecorded`).
 */
function outcomeOf(item: PlanItem): { label: string; tone: StatusTone } {
    const outcome = resultOf(item)?.outcome
    if (outcome === 'failed') return { label: 'Failed', tone: 'failed' }
    if (outcome === 'applied') return { label: 'Applied', tone: 'completed' }
    return { label: 'Not applied', tone: 'neutral' }
}

/** The decision history, oldest first, the way it happened. */
const gates = computed(() => [...(plan.value?.gates ?? [])].sort((a, b) => a.createdAt.localeCompare(b.createdAt)))

/**
 * The decision `GET /plans/:id` names as open, and only while the plan is waiting on the CEO. A plan in any other
 * status has nothing to decide here, whatever its run still has waiting.
 */
const namedGate = computed<GateRow | null>(() =>
    plan.value?.status === PLAN_STATUS_AWAITING_CEO ? (plan.value.openGate ?? null) : null,
)

/**
 * The decision the server said is no longer open (`gate_not_waiting`, #1985): it was taken somewhere else, or closed
 * without one, while this page was open. Never offered here again, even when the plan could not be read afresh and
 * the page still holds the copy that named it open: another click could only be refused again.
 */
const closedGateId = ref<string | null>(null)

/** The decision this page offers (#1984): the one the plan names as open, unless the server has said it is closed. */
const openGate = computed<GateRow | null>(() =>
    namedGate.value && namedGate.value.id !== closedGateId.value ? namedGate.value : null,
)

/**
 * The plan's open decision is a Needs-you item this page opens (#1774): reported viewed the first time the page shows
 * it, and not again when the plan is read afresh. A failed report changes nothing here.
 */
const reportViewed = viewedOncePerPage(reportNeedsYouItemViewed)
watch(
    () => openGate.value?.id ?? null,
    gateId => {
        if (gateId) {
            reportViewed(gateId)
        }
    },
    { immediate: true },
)

/**
 * Waiting on the CEO, but the proposing run has not asked yet: built-in tasks write the plan first and only then
 * ask, so this is a brief, normal state and the page says so rather than offering a decision nobody can take.
 */
const awaitingAsk = computed(() => plan.value?.status === PLAN_STATUS_AWAITING_CEO && namedGate.value === null)

/** A decision is on its way to the server: every button waits for its answer. */
const deciding = ref(false)
/** Why the server refused the last decision, shown on the page; the plan itself is left exactly as it was. */
const decisionError = ref<string | null>(null)

/** The API's answer to a decision on a gate that is no longer waiting: decided or closed elsewhere (#1985). */
const GATE_NOT_WAITING = 'gate_not_waiting'

/** The gate statuses that mean somebody decided it, as opposed to it closing without a decision (`cancelled`). */
const DECIDED_GATE_STATUSES: ReadonlySet<GateRow['status']> = new Set(['approved', 'rejected', 'changes_requested'])

/**
 * The last click the server refused as no longer open (#1985), and whether the plan could be read afresh after it.
 * Null when there is nothing to say: no such refusal, or a later decision has been sent since.
 */
const staleDecision = ref<{ refreshed: boolean } | null>(null)

/**
 * What the page says in place of the refused decision (#1985). The wording comes from the fresh read, never from the
 * refusal itself (as `decisionRefusal` never guesses a cause): "already decided" only when the refused gate now reads
 * as decided, "no longer open" when it closed without a decision (timed out, its run ended), and, when the plan could
 * not be read again, only what is known: the click was not recorded and the page is out of date.
 */
const staleDecisionMessage = computed<string | null>(() => {
    if (!staleDecision.value) return null
    if (!staleDecision.value.refreshed) {
        return 'Your decision was not recorded: this decision is no longer open, and the page could not be refreshed. Reload the page to see where this plan stands.'
    }
    const refused = plan.value?.gates?.find(gate => gate.id === closedGateId.value)
    if (refused && DECIDED_GATE_STATUSES.has(refused.status)) {
        return 'This plan was already decided somewhere else, so your decision was not recorded. The status and decision history below are current.'
    }
    return 'This decision is no longer open, so your decision was not recorded. The status and decision history below are current.'
})

/**
 * The note sent with the decision, kept under the gate card's own per-gate draft key, so a note started on either
 * surface follows the CEO to the other. Cleared once a decision is accepted, as the card clears it.
 */
const note = useStorage(() => gateDecisionDraftKey(openGate.value?.id ?? ''), '', undefined, {
    flush: 'sync',
    writeDefaults: false,
    onError: () => {
        // Keep the field usable when browser storage is unavailable.
    },
})

/** Request changes is feedback, so it needs a note, as on the card. */
const noteWritten = computed(() => note.value.trim().length > 0)

const noteLabel = computed(() =>
    openGate.value?.requestChanges ? 'Note (required for Request changes: what must change)' : 'Note (optional)',
)

/**
 * Approve on a question gate (`os.ask_ceo`) accepts every recommendation: the very body the gate card sends when each
 * question is accepted and the answers are submitted. Answering one by one stays on the card.
 */
function acceptAll(questions: CeoQuestion[]): CeoQuestionAnswer[] {
    return questions.map(question => ({
        number: question.number,
        answer: question.ctoRecommendation,
        requiresMoreDetail: false,
    }))
}

/** Whether the server refused a decision because its gate is no longer waiting: by its code, never by 409 alone. */
function isGateNotWaiting(err: unknown): boolean {
    return err instanceof Error && err.message === GATE_NOT_WAITING
}

/** Reads the plan again so its status and history come from the server, as they would after a reload. */
async function rereadPlan(): Promise<boolean> {
    try {
        plan.value = await api.getPlan(String(route.params.id))
        return true
    } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load plan'
        return false
    }
}

/**
 * The decision was already taken somewhere else, or closed without one, while this page was open (#1985). The server
 * recorded nothing. That decision is never offered again here, the plan is read afresh, and the page says what
 * happened in place of the buttons. The note draft is kept, as for any refusal (#1984).
 */
async function settleStaleDecision(gateId: string): Promise<void> {
    closedGateId.value = gateId
    const refreshed = await rereadPlan()
    staleDecision.value = { refreshed }
}

/**
 * Decides the open gate exactly as the gate card does, then reads the plan again so its status and history come
 * from the server, as they would after a reload. A refusal that leaves the decision open (a blank note, not allowed,
 * Request changes not routed, a server error) is shown, and nothing else changes: no re-read, the buttons stay usable
 * and the note is kept. A refusal because the decision is no longer open takes `settleStaleDecision` instead.
 */
async function decide(decision: GateDecision): Promise<void> {
    const gate = openGate.value
    if (!gate || deciding.value) return
    deciding.value = true
    decisionError.value = null
    // #1796: a decision resumes the run that asked, which is model work, so without an AI account nothing is sent and
    // the shell's "Connect your AI account to start" prompt opens. The plan, the note and the buttons stay as they were.
    if (!(await requireAiAccount())) {
        deciding.value = false
        return
    }
    staleDecision.value = null
    try {
        const questions = decision === 'approve' ? ceoQuestions(gate) : null
        if (questions) {
            await api.decideGate(gate.id, decision, note.value, acceptAll(questions))
        } else {
            await api.decideGate(gate.id, decision, note.value)
        }
    } catch (err) {
        if (isGateNotWaiting(err)) {
            await settleStaleDecision(gate.id)
        } else {
            decisionError.value = err instanceof Error ? err.message : 'The decision was refused.'
        }
        deciding.value = false
        return
    }
    clearGateDecisionDraft(gate.id)
    await rereadPlan()
    deciding.value = false
}

const CHANGE_LABELS: Record<string, string> = {
    new: 'New',
    changed: 'Changed',
    removed: 'Removed',
    unchanged: 'Unchanged',
}

/**
 * The kit status tone each change is drawn in. The pill says the word and draws a glyph beside the hue,
 * so the colour never carries the change alone: cyan for what the plan adds, amber for what it alters,
 * magenta for what it takes away, grey for what it leaves alone. The same mapping serves a diff field.
 */
const CHANGE_TONES: Record<string, StatusTone> = {
    new: 'system',
    added: 'system',
    changed: 'action',
    removed: 'failed',
    unchanged: 'neutral',
}

/** The tone a change or diff status is drawn in; anything unforeseen reads as neutral rather than as a state. */
function toneOf(change: string): StatusTone {
    return CHANGE_TONES[change] ?? 'neutral'
}

/** The thing an item is about, read off its own type (`create_task` becomes `task`, `delete_team` becomes `team`, ...). */
function subjectOf(item: PlanItem): string {
    return item.type.split('_')[1] ?? ''
}

/**
 * What the item does, read off its own verb (see `changeVerbOf`): every `os_change` type is
 * `create_`, `update_` or `delete_` of a task, role, team or member, so a role or team item reads
 * the same way a task does.
 */
function changeOf(item: PlanItem): string {
    return changeVerbOf(item.type)
}

function titleOf(item: PlanItem): string {
    return (
        (typeof item.name === 'string' && item.name) ||
        (typeof item.slug === 'string' && item.slug) ||
        (typeof item.email === 'string' && item.email) ||
        item.handle
    )
}

/** An item's own fields, listed plainly: what a creation shows, having nothing to diff against. */
function detailsOf(item: PlanItem): Array<{ key: string; label: string; value: string }> {
    return planItemDetails(item)
}

/** How each side of a diff is headed, and what a field's own change is called. */
const DIFF_LABELS: Record<PlanDiffStatus, string> = {
    added: 'Added',
    removed: 'Removed',
    changed: 'Changed',
    unchanged: 'Unchanged',
}

/** Every item's diff, built once per plan rather than per render, keyed by the item's handle. */
const diffs = computed<Record<string, PlanDiffField[]>>(() => {
    const built: Record<string, PlanDiffField[]> = {}
    for (const item of items.value) {
        const current = currentRecords.value[item.handle]
        if (current) built[item.handle] = diffPlanItem(item, current)
    }
    return built
})

/** What this item changes, field by field — empty for a creation, which is shown plainly instead. */
function diffOf(item: PlanItem): PlanDiffField[] {
    return diffs.value[item.handle] ?? []
}

/**
 * The items whose unchanged fields are shown, by handle. They start hidden: an update names a field or two and
 * leaves the rest alone, so the rows that say nothing changes would otherwise bury the ones being signed off.
 */
const unchangedShown = ref<Set<string>>(new Set())

function showsUnchanged(item: PlanItem): boolean {
    return unchangedShown.value.has(item.handle)
}

function toggleUnchanged(item: PlanItem): void {
    const shown = new Set(unchangedShown.value)
    if (!shown.delete(item.handle)) shown.add(item.handle)
    unchangedShown.value = shown
}

/** How many of this item's fields stay exactly as they are. */
function unchangedCountOf(item: PlanItem): number {
    return diffOf(item).filter(field => field.status === 'unchanged').length
}

/** The diff rows on the page for this item: what it changes, and what it leaves alone only once asked for. */
function visibleDiffOf(item: PlanItem): PlanDiffField[] {
    const fields = diffOf(item)
    return showsUnchanged(item) ? fields : fields.filter(field => field.status !== 'unchanged')
}

function unchangedToggleLabel(item: PlanItem): string {
    const count = unchangedCountOf(item)
    const noun = count === 1 ? 'unchanged field' : 'unchanged fields'
    return showsUnchanged(item) ? `Hide ${count} ${noun}` : `Show ${count} ${noun}`
}

function formatDate(value: string | null | undefined): string {
    if (!value) return '—'
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}
</script>

<template>
	<section
		class="w-full min-w-0 space-y-5 p-4 md:p-8"
		:class="{ 'plan-detail-mobile': isMobile }"
		data-testid="plan-detail"
	>
		<ZhPageTitle>Plan</ZhPageTitle>
		<p v-if="error" class="text-destructive text-sm" data-testid="plan-error">{{ error }}</p>
		<template v-if="plan">
			<header class="flex flex-col gap-2">
				<h2 class="text-lg font-semibold break-words" data-testid="plan-summary">
					{{ plan.summary || plan.ceoIntent || plan.id }}
				</h2>
				<ul class="text-muted-foreground flex flex-wrap gap-3 text-xs">
					<li data-testid="plan-status">Status: <strong>{{ plan.status }}</strong></li>
					<li data-testid="plan-kind">Kind: <strong>{{ plan.kind }}</strong></li>
					<li v-if="plan.decidedAt">Decided: <strong>{{ formatDate(plan.decidedAt) }}</strong></li>
					<li>Raised: <strong>{{ formatDate(plan.createdAt) }}</strong></li>
				</ul>
				<p
					v-if="plan.ceoIntent && plan.ceoIntent !== plan.summary"
					class="text-muted-foreground text-sm break-words"
					data-testid="plan-intent"
				>
					Asked for: {{ plan.ceoIntent }}
				</p>
				<p v-if="plan.run" class="text-muted-foreground text-sm">
					<RouterLink
						:to="`/runs/${plan.run.id}`"
						class="hover:text-foreground underline underline-offset-2"
						data-testid="plan-run-link"
					>
						Open the run that proposed this
					</RouterLink>
				</p>
			</header>

			<!-- #1985: where the panel was, once the server has said the decision clicked here is no longer open. -->
			<p
				v-if="staleDecisionMessage"
				class="text-muted-foreground text-sm break-words"
				role="status"
				data-testid="plan-decision-stale"
			>
				{{ staleDecisionMessage }}
			</p>

			<!-- #1984: directly under the header, so the decision is never buried under a long diff. -->
			<ZhPanel
				v-if="openGate"
				class="flex min-w-0 flex-col gap-3"
				aria-label="Decide this plan"
				data-testid="plan-decision"
			>
				<ZhSectionHeading as="h3">Decide this plan</ZhSectionHeading>
				<div class="flex flex-col gap-2">
					<Label for="plan-decision-note">{{ noteLabel }}</Label>
					<Textarea
						id="plan-decision-note"
						v-model="note"
						rows="3"
						:disabled="deciding"
						data-testid="plan-decision-note"
					/>
				</div>
				<p
					v-if="decisionError"
					class="text-destructive text-sm break-words"
					role="alert"
					data-testid="plan-decision-error"
				>
					{{ decisionError }}
				</p>
				<div class="flex flex-wrap gap-2">
					<ZhButton :disabled="deciding" data-testid="plan-decision-approve" @click="decide('approve')">
						Approve
					</ZhButton>
					<ZhButton
						variant="ghost"
						:disabled="deciding"
						data-testid="plan-decision-reject"
						@click="decide('reject')"
					>
						Reject
					</ZhButton>
					<ZhButton
						v-if="openGate.requestChanges"
						variant="ghost"
						:disabled="deciding || !noteWritten"
						data-testid="plan-decision-request-changes"
						@click="decide('request_changes')"
					>
						Request changes
					</ZhButton>
				</div>
			</ZhPanel>
			<p v-else-if="awaitingAsk" class="text-muted-foreground text-sm" data-testid="plan-decision-none">
				No decision is open for this plan yet. It is waiting for the run that proposed it to ask.
			</p>

			<ToolConnectionsWarning
				v-if="missingConnections.length"
				:missing-connections="missingConnections"
				data-testid="plan-missing-connections"
			/>

			<p v-if="applyFailed" class="text-destructive text-sm break-words" data-testid="plan-apply-failed">
				<template v-if="failedResult">
					Approved, but the change was not saved: applying it stopped at item '{{ failedResult.handle }}'.
					What stopped it is shown on that item below.
				</template>
				<template v-else>Approved, but the change was not saved: its apply ended in error.</template>
			</p>

			<p v-if="applyDeferred" class="text-muted-foreground text-sm break-words" data-testid="plan-apply-deferred">
				Approved. The task that proposed this plan now acts on it. It is not an org change, so approving it
				changed no task, role, team or member.
			</p>

			<p v-if="missingTasks.length" class="text-destructive text-sm" data-testid="plan-missing-tasks">
				Couldn't load {{ missingTasks.length === 1 ? 'a task' : 'tasks' }} this plan changes, so
				{{ missingTasks.length === 1 ? 'it is' : 'they are' }} not drawn below: {{ missingTasks.join(', ') }}.
			</p>

			<section v-if="showGraph" class="flex min-w-0 flex-col gap-3" aria-label="Proposed workflow">
				<ZhSectionHeading as="h3">Proposed workflow</ZhSectionHeading>
				<PlanGraph :graph="graph" />
			</section>

			<section v-if="items.length" class="flex min-w-0 flex-col gap-3" aria-label="What this plan changes">
				<ZhSectionHeading as="h3">What this plan changes</ZhSectionHeading>
				<ul class="flex flex-col gap-3">
					<ZhPanel
						v-for="item in items"
						:key="item.handle"
						as="li"
						class="min-w-0"
						data-testid="plan-item"
						:data-item-change="changeOf(item)"
						:data-item-type="item.type"
					>
						<header class="flex flex-wrap items-baseline gap-2">
							<ZhStatusPill :tone="toneOf(changeOf(item))" :pulse="false">
								{{ CHANGE_LABELS[changeOf(item)] }}
							</ZhStatusPill>
							<span class="text-muted-foreground text-xs">{{ subjectOf(item) }}</span>
							<strong class="min-w-0 break-words">{{ titleOf(item) }}</strong>
							<ZhStatusPill
								v-if="applyRecorded"
								:tone="outcomeOf(item).tone"
								:pulse="false"
								data-testid="plan-item-outcome"
							>
								{{ outcomeOf(item).label }}
							</ZhStatusPill>
						</header>
						<p
							v-if="resultOf(item)?.error"
							class="text-destructive mt-2 text-sm break-words"
							data-testid="plan-item-error"
						>
							{{ resultOf(item)?.error }}
						</p>
						<p v-if="planItemNote(item)" class="text-muted-foreground mt-2 text-sm" data-testid="plan-item-note">
							{{ planItemNote(item) }}
						</p>
						<div v-if="diffOf(item).length" class="mt-3 flex min-w-0 flex-col items-start gap-3">
							<ul
								v-if="visibleDiffOf(item).length"
								class="flex flex-col gap-3 self-stretch text-sm"
								data-testid="plan-diff"
							>
								<li
									v-for="field in visibleDiffOf(item)"
									:key="field.key"
									class="flex min-w-0 flex-col gap-1"
									:class="[`plan-diff-${field.status}`, { 'opacity-60': field.status === 'unchanged' }]"
									data-testid="plan-diff-field"
									:data-diff-key="field.key"
									:data-diff-status="field.status"
								>
									<div class="flex flex-wrap items-baseline gap-2">
										<span class="text-muted-foreground">{{ field.label }}</span>
										<ZhStatusPill :tone="toneOf(field.status)" :pulse="false">
											{{ DIFF_LABELS[field.status] }}
										</ZhStatusPill>
									</div>
									<!-- #239: current beside proposed on a wide screen; a phone stacks them rather than drop one. -->
									<div
										class="grid min-w-0 gap-x-3 gap-y-1.5"
										:class="isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'"
									>
										<div class="flex min-w-0 flex-col gap-0.5" data-testid="plan-diff-current">
											<ZhSystemLabel as="span" tone="muted">Now</ZhSystemLabel>
											<span
												class="break-words whitespace-pre-wrap"
												:class="{ 'line-through': field.status === 'removed' }"
											>{{ field.current }}</span>
										</div>
										<div class="flex min-w-0 flex-col gap-0.5" data-testid="plan-diff-proposed">
											<ZhSystemLabel as="span" tone="muted">Proposed</ZhSystemLabel>
											<span class="break-words whitespace-pre-wrap">{{ field.proposed }}</span>
										</div>
									</div>
								</li>
							</ul>
							<ZhButton
								v-if="unchangedCountOf(item)"
								variant="ghost"
								data-testid="plan-diff-toggle-unchanged"
								:aria-expanded="showsUnchanged(item)"
								@click="toggleUnchanged(item)"
							>
								{{ unchangedToggleLabel(item) }}
							</ZhButton>
						</div>
						<dl v-else class="mt-3">
							<ZhKeyValue v-for="detail in detailsOf(item)" :key="detail.key" :label="detail.label">
								{{ detail.value }}
							</ZhKeyValue>
						</dl>
					</ZhPanel>
				</ul>
			</section>

			<section v-else class="flex min-w-0 flex-col gap-3" aria-label="Plan payload">
				<ZhSectionHeading as="h3">Payload</ZhSectionHeading>
				<ZhJson :value="plan.payload ?? null" label="Plan payload" data-testid="plan-payload" />
			</section>

			<section class="flex min-w-0 flex-col gap-3" aria-label="Decision history">
				<ZhSectionHeading as="h3">Decision history</ZhSectionHeading>
				<ul v-if="gates.length" class="flex flex-col gap-3">
					<ZhPanel
						v-for="gate in gates"
						:key="gate.id"
						as="li"
						class="flex flex-wrap items-baseline gap-3 text-sm"
						data-testid="plan-gate"
					>
						<ZhSystemLabel as="span" tone="muted">{{ gate.status }}</ZhSystemLabel>
						<span class="min-w-0 flex-1 break-words">{{ gate.summary || gate.toolName }}</span>
						<span>{{ gate.owner }}</span>
						<time :datetime="gate.createdAt">{{ formatDate(gate.createdAt) }}</time>
					</ZhPanel>
				</ul>
				<FirstRunHint v-else lead="Nobody has decided on this plan yet." data-testid="plan-gates-empty" />
			</section>
		</template>
	</section>
</template>

